import { AudioSettings, CameraDevice, CaptureSource, QualityConfig } from '@/types';
import { audioService } from './audioService';

export interface RecordingState {
  isRecording: boolean;
  isPaused: boolean;
  isScreenSharing?: boolean;
  durationSec: number;
  recordedBytes: number;
  fps: number;
  health: 'optimal' | 'warning' | 'error';
  sourceName: string;
}

export class RecordingEngine {
  private mediaRecorder: MediaRecorder | null = null;
  private recordedChunks: Blob[] = [];
  private activeStream: MediaStream | null = null;
  private screenStream: MediaStream | null = null;
  private micStream: MediaStream | null = null;
  private currentSourceType: 'screen' | 'window' | 'camera' | 'screen-camera' = 'screen';
  private cameraStream: MediaStream | null = null;
  private sharedScreenStream: MediaStream | null = null;
  private isSharingScreen: boolean = false;
  private pipCanvas: HTMLCanvasElement | null = null;
  private pipAnimId: number | null = null;
  private pipScreenVideo: HTMLVideoElement | null = null;
  private pipCameraVideo: HTMLVideoElement | null = null;
  private timerInterval: number | null = null;
  private startTime: number = 0;
  private pausedDuration: number = 0;
  private pauseStartTime: number = 0;
  private recordedBytes: number = 0;
  private frameCount: number = 0;
  private currentFps: number = 60;
  private fpsInterval: number | null = null;

  private onStateChangeCb: ((state: RecordingState) => void) | null = null;

  public async getAvailableSources(): Promise<CaptureSource[]> {
    if (window.electronAPI && typeof window.electronAPI.getSources === 'function') {
      try {
        return await window.electronAPI.getSources();
      } catch (err) {
        console.warn('Electron getSources failed, falling back to browser display media', err);
      }
    }

    // Web Fallback: Mock or standard prompt sources
    return [
      {
        id: 'web_screen_prompt',
        name: 'Entire Screen / Application Window (Browser Prompt)',
        type: 'screen',
      },
      {
        id: 'web_meeting_window',
        name: 'Active Meeting Browser Tab / Window',
        type: 'window',
      }
    ];
  }

  public async getCameraDevices(): Promise<CameraDevice[]> {
    if (typeof navigator === 'undefined' || !navigator.mediaDevices || typeof navigator.mediaDevices.enumerateDevices !== 'function') {
      return [{ deviceId: 'default', label: 'Default Integrated Webcam' }];
    }
    try {
      let devices = await navigator.mediaDevices.enumerateDevices();
      let videoInputs = devices.filter(d => d.kind === 'videoinput');

      // If device labels are empty, try requesting temporary video permission so labels are populated
      if (videoInputs.length > 0 && !videoInputs[0].label) {
        try {
          const temp = await navigator.mediaDevices.getUserMedia({ video: true, audio: false });
          devices = await navigator.mediaDevices.enumerateDevices();
          temp.getTracks().forEach(t => t.stop());
          videoInputs = devices.filter(d => d.kind === 'videoinput');
        } catch (e) {
          console.warn('Camera permission prompt during enumeration:', e);
        }
      }

      if (videoInputs.length === 0) {
        return [{ deviceId: 'default', label: 'Default Integrated Webcam' }];
      }

      return videoInputs.map((d, index) => ({
        deviceId: d.deviceId || `camera_${index}`,
        label: d.label || `Camera ${index + 1} (${d.deviceId.slice(0, 5)}...)`,
        groupId: d.groupId
      }));
    } catch (err) {
      console.warn('Could not enumerate cameras:', err);
      return [{ deviceId: 'default', label: 'Default Integrated Webcam' }];
    }
  }

  private getOrCreateHelperContainer(): HTMLElement {
    let container = document.getElementById('meetsplit-video-pipeline-container');
    if (!container) {
      container = document.createElement('div');
      container.id = 'meetsplit-video-pipeline-container';
      container.style.cssText = 'position:fixed;top:-9999px;left:-9999px;width:2px;height:2px;opacity:0.001;pointer-events:none;overflow:hidden;z-index:-9999;';
      document.body.appendChild(container);
    }
    return container;
  }

  private async acquireCameraStream(cameraId?: string, existingStream?: MediaStream | null): Promise<MediaStream> {
    // 1. If an active camera stream is already running, reuse it directly to avoid Windows exclusive driver lock
    if (existingStream && existingStream.getVideoTracks().length > 0) {
      const liveTrack = existingStream.getVideoTracks().find(t => t.readyState === 'live');
      if (liveTrack) {
        return existingStream;
      }
    }

    // 2. Request optimal 30fps camera stream (never 60fps on webcams to avoid severe driver stutter and frame drops)
    const videoConstraints: MediaTrackConstraints = {
      deviceId: cameraId && cameraId !== 'default' && cameraId !== 'web_camera_default'
        ? { exact: cameraId }
        : undefined,
      width: { ideal: 1920, max: 1920 },
      height: { ideal: 1080, max: 1080 },
      frameRate: { ideal: 30, max: 30 }
    };

    try {
      return await navigator.mediaDevices.getUserMedia({
        video: videoConstraints,
        audio: false
      });
    } catch (err) {
      console.warn('1080p camera capture failed, attempting 720p fallback:', err);
      try {
        return await navigator.mediaDevices.getUserMedia({
          video: {
            deviceId: cameraId && cameraId !== 'default' && cameraId !== 'web_camera_default'
              ? { exact: cameraId }
              : undefined,
            width: { ideal: 1280 },
            height: { ideal: 720 },
            frameRate: { ideal: 30 }
          },
          audio: false
        });
      } catch (err720) {
        console.warn('720p camera fallback failed, requesting basic camera:', err720);
        return await navigator.mediaDevices.getUserMedia({
          video: true,
          audio: false
        });
      }
    }
  }

  public async startRecording(
    selectedSource: CaptureSource | null,
    audioSettings: AudioSettings,
    quality: QualityConfig,
    onStateChange: (state: RecordingState) => void,
    existingCameraStream?: MediaStream | null
  ): Promise<MediaStream> {
    this.onStateChangeCb = onStateChange;
    this.recordedChunks = [];
    this.recordedBytes = 0;
    this.pausedDuration = 0;
    this.currentSourceType = selectedSource?.type || 'screen';

    let screenStream: MediaStream;

    // SCREEN + CIRCLE CAMERA (PIP) MODE - Record screen with webcam pinned in bottom-right circle
    if (selectedSource?.type === 'screen-camera' || (selectedSource?.pipEnabled && selectedSource?.type !== 'camera')) {
      // 1. Acquire raw screen stream
      let rawScreenStream: MediaStream;
      const screenId = selectedSource.screenSourceId || (selectedSource.type !== 'screen-camera' ? selectedSource.id : null);
      if (window.electronAPI && screenId && screenId !== 'web_screen_prompt') {
        try {
          const constraints = {
            audio: audioSettings.systemAudioEnabled ? {
              mandatory: { chromeMediaSource: 'desktop' }
            } : false,
            video: {
              mandatory: {
                chromeMediaSource: 'desktop',
                chromeMediaSourceId: screenId,
                minWidth: quality.width,
                maxWidth: quality.width,
                minHeight: quality.height,
                maxHeight: quality.height,
                maxFrameRate: quality.fps
              }
            }
          } as unknown as MediaStreamConstraints;
          rawScreenStream = await navigator.mediaDevices.getUserMedia(constraints);
        } catch (e) {
          rawScreenStream = await navigator.mediaDevices.getDisplayMedia({
            video: { width: quality.width, height: quality.height, frameRate: quality.fps },
            audio: audioSettings.systemAudioEnabled
          });
        }
      } else {
        rawScreenStream = await navigator.mediaDevices.getDisplayMedia({
          video: { width: quality.width, height: quality.height, frameRate: quality.fps },
          audio: audioSettings.systemAudioEnabled
        });
      }

      // 2. Acquire camera stream safely with fallback & reuse
      const rawCameraStream = await this.acquireCameraStream(selectedSource.cameraId, existingCameraStream);
      this.cameraStream = rawCameraStream;

      // 3. Create High-Performance Canvas Compositor
      const canvas = document.createElement('canvas');
      canvas.width = quality.width || 1920;
      canvas.height = quality.height || 1080;
      const ctx = canvas.getContext('2d', { alpha: false, desynchronized: true })!;
      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = 'high';
      this.pipCanvas = canvas;

      const helperContainer = this.getOrCreateHelperContainer();

      const screenVideo = document.createElement('video');
      screenVideo.muted = true;
      screenVideo.playsInline = true;
      screenVideo.autoplay = true;
      screenVideo.srcObject = rawScreenStream;
      helperContainer.appendChild(screenVideo);
      await screenVideo.play().catch(() => {});
      this.pipScreenVideo = screenVideo;

      const cameraVideo = document.createElement('video');
      cameraVideo.muted = true;
      cameraVideo.playsInline = true;
      cameraVideo.autoplay = true;
      cameraVideo.srcObject = rawCameraStream;
      helperContainer.appendChild(cameraVideo);
      await cameraVideo.play().catch(() => {});
      this.pipCameraVideo = cameraVideo;

      const isMirrored = selectedSource.cameraMirrored === true;

      const drawPipFrame = () => {
        if (!this.pipCanvas) return;
        const w = canvas.width;
        const h = canvas.height;

        // Draw screen video full frame
        if (screenVideo.readyState >= 2) {
          ctx.drawImage(screenVideo, 0, 0, w, h);
        } else {
          ctx.fillStyle = '#0f172a';
          ctx.fillRect(0, 0, w, h);
        }

        // Draw circular camera avatar pinned at bottom-right
        if (cameraVideo.readyState >= 2) {
          // Circle radius: ~10% of canvas width (diameter ~384px on 1080p FHD)
          const radius = Math.round(w * 0.10);
          const margin = Math.round(w * 0.025); // margin from right/bottom
          const centerX = w - margin - radius;
          const centerY = h - margin - radius;

          ctx.save();

          // Drop shadow
          ctx.shadowColor = 'rgba(0, 0, 0, 0.45)';
          ctx.shadowBlur = 24;
          ctx.shadowOffsetX = 0;
          ctx.shadowOffsetY = 8;

          // Outer glowing gradient ring
          ctx.beginPath();
          ctx.arc(centerX, centerY, radius + 5, 0, Math.PI * 2);
          ctx.fillStyle = '#6366f1';
          ctx.fill();

          ctx.shadowColor = 'transparent';

          // Circular clip mask
          ctx.beginPath();
          ctx.arc(centerX, centerY, radius, 0, Math.PI * 2);
          ctx.closePath();
          ctx.clip();

          // Horizontal mirror/flip
          if (isMirrored) {
            ctx.translate(centerX * 2, 0);
            ctx.scale(-1, 1);
          }

          // Center cover crop
          const camW = cameraVideo.videoWidth || 1280;
          const camH = cameraVideo.videoHeight || 720;
          let srcX = 0, srcY = 0, srcW = camW, srcH = camH;
          if (camW > camH) {
            srcW = camH;
            srcX = (camW - camH) / 2;
          } else {
            srcH = camW;
            srcY = (camH - camW) / 2;
          }

          ctx.drawImage(cameraVideo, srcX, srcY, srcW, srcH, centerX - radius, centerY - radius, radius * 2, radius * 2);

          ctx.restore();

          // Clean white border ring
          ctx.save();
          ctx.beginPath();
          ctx.arc(centerX, centerY, radius, 0, Math.PI * 2);
          ctx.lineWidth = 4;
          ctx.strokeStyle = '#ffffff';
          ctx.stroke();
          ctx.restore();
        }

        this.pipAnimId = requestAnimationFrame(drawPipFrame);
      };

      drawPipFrame();

      const compositedStream = canvas.captureStream(30);

      // Preserve any system audio track captured from the screen
      const screenAudioTracks = rawScreenStream.getAudioTracks();
      if (screenAudioTracks.length > 0) {
        compositedStream.addTrack(screenAudioTracks[0]);
      }

      screenStream = compositedStream;
    }
    // CAMERA ONLY / RECORD YOURSELF MODE (Default Solo Video + Dynamic Screen Sharing)
    else if (selectedSource?.type === 'camera') {
      const rawCameraStream = await this.acquireCameraStream(selectedSource.id, existingCameraStream);
      this.cameraStream = rawCameraStream;

      const isMirrored = selectedSource.cameraMirrored === true;
      const aspectRatio = selectedSource.aspectRatio || '16:9';

      const canvas = document.createElement('canvas');
      if (aspectRatio === '9:16') {
        canvas.width = 1080;
        canvas.height = 1920;
      } else if (aspectRatio === '1:1') {
        canvas.width = 1080;
        canvas.height = 1080;
      } else {
        canvas.width = quality.width || 1920;
        canvas.height = quality.height || 1080;
      }

      const ctx = canvas.getContext('2d', { alpha: false, desynchronized: true })!;
      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = 'high';
      this.pipCanvas = canvas;

      const helperContainer = this.getOrCreateHelperContainer();

      const cameraVideo = document.createElement('video');
      cameraVideo.muted = true;
      cameraVideo.playsInline = true;
      cameraVideo.autoplay = true;
      cameraVideo.srcObject = rawCameraStream;
      helperContainer.appendChild(cameraVideo);
      await cameraVideo.play().catch(() => {});
      this.pipCameraVideo = cameraVideo;

      this.isSharingScreen = false;
      this.sharedScreenStream = null;
      this.pipScreenVideo = null;

      const drawCameraSessionFrame = () => {
        if (!this.pipCanvas) return;
        const w = canvas.width;
        const h = canvas.height;

        // 1. If Screen Sharing is ACTIVE: Draw shared screen + Circle Camera in bottom-right
        if (this.isSharingScreen && this.pipScreenVideo && this.pipScreenVideo.readyState >= 2) {
          ctx.drawImage(this.pipScreenVideo, 0, 0, w, h);

          if (cameraVideo.readyState >= 2) {
            const radius = Math.round(w * 0.10);
            const margin = Math.round(w * 0.025);
            const centerX = w - margin - radius;
            const centerY = h - margin - radius;

            ctx.save();
            ctx.shadowColor = 'rgba(0, 0, 0, 0.5)';
            ctx.shadowBlur = 24;
            ctx.shadowOffsetX = 0;
            ctx.shadowOffsetY = 8;

            ctx.beginPath();
            ctx.arc(centerX, centerY, radius + 5, 0, Math.PI * 2);
            ctx.fillStyle = '#6366f1';
            ctx.fill();

            ctx.shadowColor = 'transparent';

            ctx.beginPath();
            ctx.arc(centerX, centerY, radius, 0, Math.PI * 2);
            ctx.closePath();
            ctx.clip();

            if (isMirrored) {
              ctx.translate(centerX * 2, 0);
              ctx.scale(-1, 1);
            }

            const camW = cameraVideo.videoWidth || 1280;
            const camH = cameraVideo.videoHeight || 720;
            let srcX = 0, srcY = 0, srcW = camW, srcH = camH;
            if (camW > camH) {
              srcW = camH;
              srcX = (camW - camH) / 2;
            } else {
              srcH = camW;
              srcY = (camH - camW) / 2;
            }

            ctx.drawImage(cameraVideo, srcX, srcY, srcW, srcH, centerX - radius, centerY - radius, radius * 2, radius * 2);
            ctx.restore();

            ctx.save();
            ctx.beginPath();
            ctx.arc(centerX, centerY, radius, 0, Math.PI * 2);
            ctx.lineWidth = 4;
            ctx.strokeStyle = '#ffffff';
            ctx.stroke();
            ctx.restore();
          }
        } else {
          // 2. Solo Camera Mode (Default): Full video recorded
          if (cameraVideo.readyState >= 2) {
            ctx.save();
            if (isMirrored) {
              ctx.translate(w, 0);
              ctx.scale(-1, 1);
            }

            const camW = cameraVideo.videoWidth || 1280;
            const camH = cameraVideo.videoHeight || 720;
            const targetAspect = w / h;
            const sourceAspect = camW / camH;

            let sx = 0, sy = 0, sw = camW, sh = camH;
            if (sourceAspect > targetAspect) {
              sw = camH * targetAspect;
              sx = (camW - sw) / 2;
            } else {
              sh = camW / targetAspect;
              sy = (camH - sh) / 2;
            }

            ctx.drawImage(cameraVideo, sx, sy, sw, sh, 0, 0, w, h);
            ctx.restore();
          } else {
            ctx.fillStyle = '#020617';
            ctx.fillRect(0, 0, w, h);
          }
        }

        this.pipAnimId = requestAnimationFrame(drawCameraSessionFrame);
      };

      drawCameraSessionFrame();
      screenStream = canvas.captureStream(30);
    } else if (window.electronAPI && selectedSource && selectedSource.id !== 'web_screen_prompt') {
      // Electron desktop capturer flow
      try {
        // In modern Electron, getUserMedia with chromeMediaSource
        const constraints = {
          audio: audioSettings.systemAudioEnabled ? {
            mandatory: {
              chromeMediaSource: 'desktop'
            }
          } : false,
          video: {
            mandatory: {
              chromeMediaSource: 'desktop',
              chromeMediaSourceId: selectedSource.id,
              minWidth: quality.width,
              maxWidth: quality.width,
              minHeight: quality.height,
              maxHeight: quality.height,
              maxFrameRate: quality.fps
            }
          }
        } as unknown as MediaStreamConstraints;

        screenStream = await navigator.mediaDevices.getUserMedia(constraints);
      } catch (e) {
        console.warn('Native desktop capture constraints fallback', e);
        screenStream = await navigator.mediaDevices.getDisplayMedia({
          video: { width: quality.width, height: quality.height, frameRate: quality.fps },
          audio: audioSettings.systemAudioEnabled
        });
      }
    } else {
      // Standard browser screen sharing
      screenStream = await navigator.mediaDevices.getDisplayMedia({
        video: {
          width: quality.width,
          height: quality.height,
          frameRate: quality.fps
        },
        audio: audioSettings.systemAudioEnabled
      });
    }

    this.screenStream = screenStream;

    // Capture microphone if enabled
    let micStream: MediaStream | null = null;
    if (audioSettings.micEnabled) {
      micStream = await audioService.getMicrophoneStream(audioSettings.micDeviceId);
      this.micStream = micStream;
    }

    // Initialize Audio meters
    const sysStream = screenStream.getAudioTracks().length > 0 ? screenStream : null;
    audioService.initMeters(micStream, sysStream);

    // Composite media stream
    const compositeTracks: MediaStreamTrack[] = [];
    const videoTrack = screenStream.getVideoTracks()[0];
    if (videoTrack) compositeTracks.push(videoTrack);

    // Audio mixing with dual-channel Host (Mic) & Guest (System) isolation
    const audioStreamsToMix: MediaStream[] = [];
    if (sysStream) audioStreamsToMix.push(sysStream);
    if (micStream) audioStreamsToMix.push(micStream);

    if (audioStreamsToMix.length > 0) {
      const mixedTrack = audioService.mixAudioStreams(audioStreamsToMix, micStream, sysStream);
      if (mixedTrack) compositeTracks.push(mixedTrack);
    }

    const finalStream = new MediaStream(compositeTracks);
    this.activeStream = finalStream;

    // Pick supported MIME type
    const mimeTypes = [
      'video/webm;codecs=vp9,opus',
      'video/webm;codecs=vp8,opus',
      'video/webm;codecs=h264,opus',
      'video/webm',
      'video/mp4'
    ];

    let chosenMime = '';
    for (const mime of mimeTypes) {
      if (MediaRecorder.isTypeSupported(mime)) {
        chosenMime = mime;
        break;
      }
    }

    // High fidelity recording with 192kbps audio & optimized video bitrate
    const isCameraSolo = this.currentSourceType === 'camera';
    const isCameraPip = this.currentSourceType === 'screen-camera';
    const targetVideoBitrate = (isCameraSolo || isCameraPip)
      ? 6000000 // 6 Mbps is broadcast-grade crystal clear for 1080p 30fps webcam and guarantees zero frame drops
      : (quality.bitrateBps || 15000000);

    this.mediaRecorder = new MediaRecorder(finalStream, {
      mimeType: chosenMime || undefined,
      videoBitsPerSecond: targetVideoBitrate,
      audioBitsPerSecond: 192000
    });

    this.mediaRecorder.ondataavailable = (event) => {
      if (event.data && event.data.size > 0) {
        this.recordedChunks.push(event.data);
        this.recordedBytes += event.data.size;
        this.notifyState();
      }
    };

    // Listen for user stopping screen share via browser bar
    videoTrack.onended = () => {
      this.stop();
    };

    this.mediaRecorder.start(1000); // 1-second chunks for incremental buffering
    this.startTime = Date.now();

    // Start timer & FPS tracker
    this.timerInterval = window.setInterval(() => {
      this.notifyState();
    }, 500);

    return finalStream;
  }

  public pause(): void {
    if (this.mediaRecorder && this.mediaRecorder.state === 'recording') {
      this.mediaRecorder.pause();
      this.pauseStartTime = Date.now();
      this.notifyState();
    }
  }

  public resume(): void {
    if (this.mediaRecorder && this.mediaRecorder.state === 'paused') {
      this.mediaRecorder.resume();
      if (this.pauseStartTime > 0) {
        this.pausedDuration += Date.now() - this.pauseStartTime;
        this.pauseStartTime = 0;
      }
      this.notifyState();
    }
  }

  public async stop(): Promise<{
    blob: Blob;
    durationSec: number;
    fileSize: number;
    width: number;
    height: number;
    fps: number;
  }> {
    return new Promise((resolve) => {
      if (!this.mediaRecorder || this.mediaRecorder.state === 'inactive') {
        const fallbackBlob = new Blob(this.recordedChunks, { type: 'video/webm' });
        resolve({
          blob: fallbackBlob,
          durationSec: 1,
          fileSize: fallbackBlob.size,
          width: 1920,
          height: 1080,
          fps: 30
        });
        return;
      }

      this.mediaRecorder.onstop = () => {
        const totalDurationSec = Math.max(1, Math.round((Date.now() - this.startTime - this.pausedDuration) / 1000));
        const mime = this.mediaRecorder?.mimeType || 'video/webm';
        const finalBlob = new Blob(this.recordedChunks, { type: mime });

        let width = 1920;
        let height = 1080;
        if (this.screenStream) {
          const track = this.screenStream.getVideoTracks()[0];
          if (track) {
            const settings = track.getSettings();
            if (settings.width) width = settings.width;
            if (settings.height) height = settings.height;
          }
        }

        this.cleanup();

        resolve({
          blob: finalBlob,
          durationSec: totalDurationSec,
          fileSize: finalBlob.size,
          width,
          height,
          fps: this.currentFps
        });
      };

      this.mediaRecorder.stop();
    });
  }

  public cleanup(): void {
    this.stopScreenShare();

    if (this.pipAnimId) {
      cancelAnimationFrame(this.pipAnimId);
      this.pipAnimId = null;
    }
    if (this.pipScreenVideo) {
      this.pipScreenVideo.pause();
      this.pipScreenVideo.srcObject = null;
      this.pipScreenVideo.remove();
      this.pipScreenVideo = null;
    }
    if (this.pipCameraVideo) {
      this.pipCameraVideo.pause();
      this.pipCameraVideo.srcObject = null;
      this.pipCameraVideo.remove();
      this.pipCameraVideo = null;
    }
    const container = document.getElementById('meetsplit-video-pipeline-container');
    if (container) {
      container.innerHTML = '';
      container.remove();
    }
    this.pipCanvas = null;

    if (this.cameraStream) {
      this.cameraStream.getTracks().forEach(t => t.stop());
      this.cameraStream = null;
    }

    if (this.timerInterval) {
      clearInterval(this.timerInterval);
      this.timerInterval = null;
    }
    if (this.fpsInterval) {
      clearInterval(this.fpsInterval);
      this.fpsInterval = null;
    }
    audioService.stopMeters();

    if (this.activeStream) {
      this.activeStream.getTracks().forEach(t => t.stop());
      this.activeStream = null;
    }
    if (this.screenStream) {
      this.screenStream.getTracks().forEach(t => t.stop());
      this.screenStream = null;
    }
    if (this.micStream) {
      this.micStream.getTracks().forEach(t => t.stop());
      this.micStream = null;
    }
  }

  public async startScreenShare(screenSourceId?: string): Promise<boolean> {
    try {
      let screenStream: MediaStream;

      if (window.electronAPI && screenSourceId && screenSourceId !== 'web_screen_prompt') {
        try {
          const constraints = {
            audio: false,
            video: {
              mandatory: {
                chromeMediaSource: 'desktop',
                chromeMediaSourceId: screenSourceId,
                minWidth: 1920,
                maxWidth: 1920,
                minHeight: 1080,
                maxHeight: 1080,
                maxFrameRate: 30
              }
            }
          } as unknown as MediaStreamConstraints;
          screenStream = await navigator.mediaDevices.getUserMedia(constraints);
        } catch {
          screenStream = await navigator.mediaDevices.getDisplayMedia({
            video: { width: 1920, height: 1080, frameRate: 30 },
            audio: false
          });
        }
      } else if (window.electronAPI && typeof window.electronAPI.getSources === 'function') {
        try {
          const sources = await window.electronAPI.getSources();
          const primaryScreen = sources.find(s => s.type === 'screen') || sources[0];
          if (primaryScreen) {
            const constraints = {
              audio: false,
              video: {
                mandatory: {
                  chromeMediaSource: 'desktop',
                  chromeMediaSourceId: primaryScreen.id,
                  minWidth: 1920,
                  maxWidth: 1920,
                  minHeight: 1080,
                  maxHeight: 1080,
                  maxFrameRate: 30
                }
              }
            } as unknown as MediaStreamConstraints;
            screenStream = await navigator.mediaDevices.getUserMedia(constraints);
          } else {
            screenStream = await navigator.mediaDevices.getDisplayMedia({
              video: { width: 1920, height: 1080, frameRate: 30 },
              audio: false
            });
          }
        } catch {
          screenStream = await navigator.mediaDevices.getDisplayMedia({
            video: { width: 1920, height: 1080, frameRate: 30 },
            audio: false
          });
        }
      } else {
        screenStream = await navigator.mediaDevices.getDisplayMedia({
          video: { width: 1920, height: 1080, frameRate: 30 },
          audio: false
        });
      }

      const helperContainer = this.getOrCreateHelperContainer();
      const screenVideo = document.createElement('video');
      screenVideo.muted = true;
      screenVideo.playsInline = true;
      screenVideo.autoplay = true;
      screenVideo.srcObject = screenStream;
      helperContainer.appendChild(screenVideo);
      await screenVideo.play().catch(() => {});

      const track = screenStream.getVideoTracks()[0];
      if (track) {
        track.onended = () => {
          this.stopScreenShare();
        };
      }

      this.sharedScreenStream = screenStream;
      this.pipScreenVideo = screenVideo;
      this.isSharingScreen = true;
      this.notifyState();
      return true;
    } catch (err) {
      console.warn('Screen share cancelled or error:', err);
      return false;
    }
  }

  public stopScreenShare(): void {
    if (this.sharedScreenStream) {
      this.sharedScreenStream.getTracks().forEach(t => t.stop());
      this.sharedScreenStream = null;
    }
    if (this.pipScreenVideo) {
      this.pipScreenVideo.pause();
      this.pipScreenVideo.srcObject = null;
      this.pipScreenVideo.remove();
      this.pipScreenVideo = null;
    }
    this.isSharingScreen = false;
    this.notifyState();
  }

  public isScreenSharing(): boolean {
    return this.isSharingScreen;
  }

  private notifyState(): void {
    if (!this.onStateChangeCb) return;

    const isRecording = this.mediaRecorder?.state === 'recording';
    const isPaused = this.mediaRecorder?.state === 'paused';
    
    let durationSec = 0;
    if (this.startTime > 0) {
      const now = isPaused && this.pauseStartTime > 0 ? this.pauseStartTime : Date.now();
      durationSec = Math.max(0, Math.floor((now - this.startTime - this.pausedDuration) / 1000));
    }

    this.onStateChangeCb({
      isRecording: isRecording || isPaused,
      isPaused,
      isScreenSharing: this.isSharingScreen,
      durationSec,
      recordedBytes: this.recordedBytes,
      fps: this.currentFps,
      health: 'optimal',
      sourceName: this.isSharingScreen
        ? '🖥️ Screen + ⭕ Circle Camera (Bottom-Right)'
        : this.currentSourceType === 'camera'
        ? (this.cameraStream?.getVideoTracks()[0]?.label || 'Solo Camera Video')
        : (this.screenStream?.getVideoTracks()[0]?.label || 'Screen Stream')
    });
  }
}

export const recordingEngine = new RecordingEngine();
